// src/components/dashboard/cases/newCase/case_id/case.js
// OPTIMIZED: Ek hi API call, CaseDataProvider se saara data distribute

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import AccountLabel from "./accountLabel";
import PatientLabel from "./patientLabel";
import ProviderLabel from "./providerLabel";
import AppointmentLabel from "./appointmentLabel";
import ColorLabel from "./colorLabel";
import ShipmentLabel from "./shipmentLabel";
import StatusLabel from "./statusLabel";
import TagLabel from "./tagLabel";
import CaseTab from "./caseTab";
import Production from "./Production";
import Shipping from "./shippingSection";
import axios from "axios";
import Header from "../../../../header/header";
import Navvbar from "../../../../header/Navvbar";
import ActionButton from "./actionButton/button";
import CloseInvoiceCase from "../Invoice";
import { CaseDataProvider, useCaseData, getUserData, getUserLabId } from "../../../../../context/CaseDataContext";
import GlobalStlViewer from "./caseTable/globalStlViewer";

// ============================================
// MAIN WRAPPER - Provider ke saath
// ============================================
const Case = () => {
  const { caseId } = useParams();

  return (
    <CaseDataProvider caseId={caseId}>
      <CaseContent caseId={caseId} />
      {/* Global STL viewer — mounted at provider level so it survives
          F5 / page reloads even while CaseContent is showing the
          "Loading case details..." screen. */}
      <GlobalStlViewer />
    </CaseDataProvider>
  );
};

// ============================================
// CASE CONTENT - Context se data leta hai
// ============================================
const CaseContent = ({ caseId }) => {
  const navigate = useNavigate();

  const {
    caseData,
    loading,
    error,
    remakeCases,
    accountDetails,
    refreshCaseData,
    updateCaseField,
    updateMultipleFields,
  } = useCaseData();

  const [invoiceData, setInvoiceData] = useState(null);
  const [visiblePages, setVisiblePages] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // ============================================
  // UPLOAD MODAL MINIMIZE STATE
  // ============================================
  const [isUploadModalMinimized, setIsUploadModalMinimized] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const permissionsFetched = useRef(false);

  const formatInvoiceNumber = (number) => {
    return number ? String(number).padStart(6, "0") : "N/A";
  };

  // ============================================
  // PERMISSIONS - sirf ek baar
  // ============================================
  useEffect(() => {
    if (permissionsFetched.current) return;

    const userData = getUserData();
    const adminStatus = userData.userRole === "Admin" || userData.userRole === "admin";

    setUserRole(userData.userRole);
    setIsAdmin(adminStatus);

    if (!adminStatus && userData.userId) {
      fetch(`https://hms.backend.hiragroupcloud.com/api/page/user/${userData.userId}/pages`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
        .then(res => res.ok ? res.json() : Promise.reject('Failed'))
        .then(data => {
          if (data && Array.isArray(data)) {
            const pageNames = data.map(perm => perm.page?.name || perm.pageName).filter(Boolean);
            setVisiblePages(pageNames);
          }
        })
        .catch(err => console.error("Error fetching permissions:", err));
    }

    permissionsFetched.current = true;
  }, []);

  const shouldShowPage = useCallback(
    (pageName) => isAdmin || visiblePages.includes(pageName),
    [isAdmin, visiblePages]
  );

  // ============================================
  // HANDLERS - Context ke through update
  // ============================================
  const handleAccountClick = useCallback(() => {
    if (!accountDetails) {
      alert("No account details available for this case");
      return;
    }
    navigate(`/tabsWorking/${accountDetails.accountId}`, {
      state: { accountData: accountDetails },
    });
  }, [accountDetails, navigate]);

  const handleInvoiceCreated = useCallback((newInvoice) => {
    setInvoiceData(newInvoice);
    updateCaseField('invoiceNumber', newInvoice.invoiceNumber);
  }, [updateCaseField]);

  const handleTryInUpdate = useCallback((newTryInStatus) => {
    updateCaseField('try_in', newTryInStatus);
  }, [updateCaseField]);

  const handleUpdate = useCallback((updatedCase) => {
    if (typeof updatedCase === 'object') {
      updateMultipleFields(updatedCase);
    }
  }, [updateMultipleFields]);

  const handleProviderChange = useCallback((newName) => {
    updateCaseField('provider_name', newName);
  }, [updateCaseField]);

  const handleStatusUpdate = useCallback((updatedStatus) => {
    updateCaseField('status', updatedStatus);
  }, [updateCaseField]);

  const handleReferenceCaseClick = useCallback((caseNumber) => {
    navigate(`/case/${caseNumber}`);
  }, [navigate]);

  const handleOutsourceClick = useCallback(() => {
    if (!caseData?.caseNumber) return;
    navigate(`/outsource-case/${caseData.caseNumber}`, {
      state: { caseData },
    });
  }, [caseData, navigate]);

  // ============================================
  // UPLOAD MODAL MINIMIZE HANDLERS
  // ============================================
  const handleUploadModalMinimize = useCallback(() => {
    setIsUploadModalMinimized(true);
  }, []);

  const handleUploadModalRestore = useCallback(() => {
    setIsUploadModalMinimized(false);
  }, []);

  const handleUploadModalClose = useCallback(() => {
    setIsUploadModalMinimized(false);
    setIsUploadModalOpen(false);
  }, []);

  // ============================================
  // LOADING / ERROR / NO DATA
  // ============================================
  if (loading) return <p>Loading case details...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!caseData) return <p>No case data found</p>;

  const patientFullName =
    `${caseData?.first_name || ""} ${caseData?.last_name || ""}`.trim() || "No Patient Name";

  const isOutsourced = caseData?.outsourcedTo?.length > 0;

  // ============================================
  // RENDER
  // ============================================
  return (
    <div>
      <Header />
      <Navvbar />

      <div
        className="case-header-container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "95%",
          margin: "20px auto",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <div className="case-title-section" style={{ display: "flex", alignItems: "center", gap: "15px", flexWrap: "wrap" }}>
          <h2 className="case-number" style={{ fontSize: "36px", marginBottom: "5px" }}>
            Case #{caseId}
          </h2>

          {isOutsourced && (
            <div
              className="outsourced-badge"
              onClick={handleOutsourceClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleOutsourceClick(); } }}
              title="View outsource details"
              style={{
                color: "#004aad", padding: "4px 15px", borderRadius: "20px",
                fontSize: "16px", fontWeight: "bold", display: "inline-flex",
                alignItems: "center", gap: "6px", border: "2px solid #004aad",
                boxShadow: "0 2px 4px rgba(53, 107, 255, 0.3)",
                letterSpacing: "0.5px", cursor: "pointer",
              }}
            >
              OUTSOURCED
            </div>
          )}

          {caseData?.invoiceNumber && (
            <div className="invoice-section">
              <h2 className="invoice-number" style={{ fontSize: "36px", marginBottom: "5px" }}>
                Invoice{" "}
                {shouldShowPage("Invoice Details") ? (
                  <Link
                    to={`/invoices/${formatInvoiceNumber(caseData.invoiceNumber)}`}
                    state={{ invoiceData, caseId: caseData?.caseNumber }}
                  >
                    #{formatInvoiceNumber(caseData.invoiceNumber)}
                  </Link>
                ) : (
                  <span>#{formatInvoiceNumber(caseData.invoiceNumber)}</span>
                )}
              </h2>
            </div>
          )}

          {caseData?.status === "canceled" && (
            <div className="cancelled-badge" style={{ position: "relative", display: "inline-block" }}>
              <div style={{
                fontSize: "36px", color: "red", fontWeight: "bold",
                transform: "rotate(-15deg)", border: "2px solid red",
                padding: "5px 10px", borderRadius: "5px",
                backgroundColor: "rgba(255, 0, 0, 0.1)",
              }}>
                CANCELLED
              </div>
            </div>
          )}
        </div>

        <div
          className="action-buttons-container"
          style={{
            display: "flex", gap: "10px", alignItems: "center",
            flexWrap: "wrap", marginLeft: "auto",
          }}
        >
          {!caseData?.invoiceNumber && shouldShowPage("Close/Invoice Case") && (
            <CloseInvoiceCase caseData={caseData} onInvoiceCreated={handleInvoiceCreated} />
          )}

          <ActionButton
            caseId={caseData?.case_id}
            caseNumber={caseId}
            initialArrived={!!caseData?.arrive_date}
            onTryInUpdate={handleTryInUpdate}
            onStatusUpdate={handleStatusUpdate}
          />
        </div>
      </div>

      <div style={{ width: "95%", margin: "0 auto" }}>
        {caseData?.isRemake && caseData?.remakeReference && (
          <div className="reference-info" style={{ fontSize: "14px", color: "#666", marginTop: "2px" }}>
            🔄 Remake of{" "}
            <span
              onClick={() => handleReferenceCaseClick(caseData.remakeReference.previousCaseNumber)}
              style={{ color: "#007bff", cursor: "pointer", textDecoration: "underline", fontWeight: "500" }}
            >
              Case #{caseData.remakeReference.previousCaseNumber}
            </span>
            {caseData.remakeReference.reason && (
              <span style={{ marginLeft: "8px", color: "#888" }}>
                ({caseData.remakeReference.reason})
              </span>
            )}
          </div>
        )}

        {caseData?.hasRemake && caseData?.remakeCaseNumber && (
          <div className="remade-info" style={{ fontSize: "14px", color: "#28a745", marginTop: "2px" }}>
            ✅ Remade to{" "}
            <span
              onClick={() => handleReferenceCaseClick(caseData.remakeCaseNumber)}
              style={{ color: "#007bff", cursor: "pointer", textDecoration: "underline", fontWeight: "500" }}
            >
              Case #{caseData.remakeCaseNumber}
            </span>
          </div>
        )}
      </div>

      <div
        className="main-content-container"
        style={{ marginTop: "1%", width: "95%", margin: "0 auto", padding: "0px" }}
      >
        {remakeCases.length > 0 && (
          <div
            className="remake-cases-list"
            style={{
              marginBottom: "15px", fontSize: "14px", color: "#666",
              display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
            }}
          >
            <span style={{ fontWeight: "500" }}>🔄 This case has been remade to:</span>
            {remakeCases.map((remakeCase, index) => (
              <span key={remakeCase._id || remakeCase.caseNumber}>
                <span
                  onClick={() => handleReferenceCaseClick(remakeCase.caseNumber)}
                  style={{ color: "#007bff", cursor: "pointer", textDecoration: "underline" }}
                >
                  Case #{remakeCase.caseNumber}
                </span>
                {remakeCase.remakeReference?.reason && (
                  <span style={{ color: "#888", marginLeft: "4px" }}>
                    ({remakeCase.remakeReference.reason})
                  </span>
                )}
                {index < remakeCases.length - 1 && ", "}
              </span>
            ))}
          </div>
        )}

        {/* Row 1: Account + Patient */}
        <div className="row responsive-row" style={{ width: "100%" }}>
          <div className="col-lg-5 responsive-col account-col">
            <div onClick={handleAccountClick} style={{ cursor: "pointer" }}>
              <AccountLabel account={accountDetails?.account_name || "No Account Available"} />
            </div>
          </div>
          <div className="col-lg-5 responsive-col patient-col">
            <PatientLabel
              patientName={patientFullName}
              caseId={caseId}
              caseData={caseData}
              onUpdate={handleUpdate}
            />
          </div>
        </div>

        {/* Row 2: Provider + Appointment */}
        <div className="row responsive-row" style={{ width: "100%" }}>
          <div className="col-lg-6 responsive-col provider-col">
            <ProviderLabel
              caseNumber={caseId}
              providerName={caseData?.provider_name || "No provider assigned"}
              onProviderChange={handleProviderChange}
              caseData={caseData}
            />
          </div>
          <div className="col-lg-6 responsive-col appointment-col">
            <AppointmentLabel
              caseId={caseId}
              initialAppointment={caseData?.appointment}
              caseData={caseData}
              onUpdate={handleUpdate}
            />
          </div>
        </div>

        {/* Row 3: Color + Status */}
        <div className="row responsive-row" style={{ width: "100%" }}>
          <div className="col-lg-6 responsive-col color-col">
            <ColorLabel
              panColor={caseData?.color || "No Color Info"}
              panNumber={caseData?.pan_number || ""}
              caseId={caseId}
              onUpdate={handleUpdate}
              caseData={caseData}
            />
          </div>
          <div className="col-lg-6 responsive-col status-col">
            <StatusLabel caseId={caseId} caseData={caseData} onUpdate={handleUpdate} />
            {caseData?.status === "canceled" && caseData?.cancellationReason && (
              <div style={{ marginTop: "10px", color: "red" }}>
                Cancellation Reason: {caseData.cancellationReason}
              </div>
            )}
          </div>
        </div>

        {/* Row 4: Production + Shipping */}
        <div className="row production-shipping-row" style={{ width: "100%" }}>
          <div className="col-lg-6 production-col">
            <Production caseId={caseId} caseData={caseData} />

            <div className="tag-label-wrapper" style={{ marginTop: "10px" }}>
              <TagLabel
                caseId={caseId}
                tag={caseData?.tags || "No tags available"}
                caseData={caseData}
                onUpdate={handleUpdate}
              />
            </div>

            <div className="shipment-label-wrapper" style={{ marginTop: "10px" }}>
              <ShipmentLabel caseId={caseId} caseData={caseData} onUpdate={handleUpdate} />
            </div>
          </div>

          <div className="col-lg-6 shipping-col">
            <Shipping caseId={caseId} caseData={caseData} onShippingUpdate={refreshCaseData} />
          </div>
        </div>

        {caseData?.isRemake && caseData?.remakeReference?.description && (
          <div className="row" style={{ width: "100%", marginTop: "10px" }}>
            <div className="col-lg-12">
              <div style={{
                fontSize: "14px", color: "#666", backgroundColor: "#f8f9fa",
                padding: "10px", borderRadius: "5px", border: "1px solid #dee2e6",
              }}>
                <span style={{ fontWeight: "500" }}>📝 Remake Note:</span>{" "}
                {caseData.remakeReference.description}
                {caseData.remakeReference.fullArchTemp && (
                  <span style={{
                    marginLeft: "10px", backgroundColor: "#004aad", color: "white",
                    padding: "2px 8px", borderRadius: "10px", fontSize: "12px",
                  }}>
                    Full Arch Temp
                  </span>
                )}
                {caseData.remakeReference.uber && (
                  <span style={{
                    marginLeft: "5px", backgroundColor: "#28a745", color: "white",
                    padding: "2px 8px", borderRadius: "10px", fontSize: "12px",
                  }}>
                    Uber
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CASE TABS */}
        <div className="row mt-3 case-tabs-row">
          <div className="col-lg-12">
            <CaseTab
              components={caseData?.components || "No components available"}
              caseId={caseId}
            />
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* MINIMIZED UPLOAD MODAL FLOATING BAR         */}
      {/* ============================================ */}
      {isUploadModalMinimized && (
        <div
          className="upload-modal-minimized-bar"
          onClick={handleUploadModalRestore}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleUploadModalRestore();
            }
          }}
          title="Click to restore upload window"
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 99999,
            background: "linear-gradient(135deg, #004aad 0%, #0066cc 100%)",
            color: "white",
            padding: "10px 16px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
            boxShadow: "0 4px 15px rgba(0, 74, 173, 0.4)",
            fontSize: "14px",
            fontWeight: "600",
            transition: "all 0.2s ease",
            userSelect: "none",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 74, 173, 0.6)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 15px rgba(0, 74, 173, 0.4)";
          }}
        >
          <span style={{ fontSize: "18px" }}>📤</span>
          <span>Upload Window</span>
          <span
            style={{
              background: "rgba(255,255,255,0.25)",
              borderRadius: "50%",
              width: "22px",
              height: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              marginLeft: "4px",
            }}
          >
            ▲
          </span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              handleUploadModalClose();
            }}
            title="Close upload window"
            style={{
              background: "rgba(255,255,255,0.25)",
              borderRadius: "50%",
              width: "22px",
              height: "22px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              marginLeft: "2px",
              cursor: "pointer",
            }}
          >
            ✕
          </span>
        </div>
      )}

      <style>
        {`
          .account-col > div > div { margin-bottom: 0 !important; }
          .account-col p { margin-bottom: 0 !important; }
          .account-col div[style*="display: flex"] { margin-top: 0 !important; }
          .responsive-col:has(> div > p:contains("Provider:")) p { margin-bottom: 0 !important; }
          .responsive-col:has(> div > h5) h5 { margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important; }
          .responsive-col:has(> div > h5) > div { margin-top: 0 !important; }
          .color-col > div > p { margin-bottom: 0 !important; }
          .color-col div[style*="display: flex"] { margin-top: 0 !important; }
          .color-col div[style*="marginTop: 2px"] { margin-top: 0 !important; }
          .color-col input { margin-top: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important; }

          @media (min-width: 1025px) {
            .account-col { flex: 0 0 45% !important; max-width: 45% !important; padding-right: 0 !important; margin-right: 0 !important; }
            .patient-col { flex: 0 0 45% !important; max-width: 45% !important; padding-left: 0 !important; margin-left: 95px !important; }
            .patient-col * { margin-left: 0 !important; padding-left: 0 !important; }
            .account-col * { margin-left: 0 !important; padding-left: 0 !important; }
          }

          @media (min-width: 769px) and (max-width: 1024px) {
            .responsive-row { display: flex !important; flex-wrap: wrap !important; align-items: flex-start !important; gap: 0 !important; margin-bottom: 6px !important; width: 100% !important; }
            .responsive-col { flex: 1 1 50% !important; min-width: 0 !important; max-width: 50% !important; margin: 0 !important; padding: 0 !important; align-self: flex-start !important; box-sizing: border-box !important; }
            .account-col { flex: 0 0 50% !important; max-width: 50% !important; padding-right: 8px !important; margin: 0 !important; }
            .patient-col { flex: 0 0 50% !important; max-width: 50% !important; padding-left: 8px !important; margin: 0 !important; }
            .account-col *, .patient-col * { margin-left: 0 !important; padding-left: 0 !important; }
            .provider-col { flex: 0 0 50% !important; max-width: 50% !important; padding-right: 8px !important; margin: 0 !important; }
            .appointment-col { flex: 0 0 50% !important; max-width: 50% !important; padding-left: 8px !important; margin: 0 !important; }
            .color-col { flex: 0 0 50% !important; max-width: 50% !important; padding-right: 8px !important; margin: 0 !important; }
            .status-col { flex: 0 0 50% !important; max-width: 50% !important; padding-left: 8px !important; margin: 0 !important; }
            .production-shipping-row { display: flex !important; flex-wrap: wrap !important; align-items: flex-start !important; gap: 0 !important; width: 100% !important; margin: 0 !important; padding: 0 !important; }
            .production-col { flex: 0 0 50% !important; max-width: 50% !important; padding-right: 8px !important; margin: 0 !important; }
            .shipping-col { flex: 0 0 50% !important; max-width: 50% !important; padding-left: 8px !important; margin: 0 !important; }
            .case-header-container { width: 95% !important; margin: 12px auto !important; gap: 6px !important; }
            .case-number { font-size: 28px !important; }
            .invoice-number { font-size: 28px !important; }
            .case-title-section { gap: 10px !important; }
            .main-content-container { width: 95% !important; margin-top: 6px !important; }
            .reference-info, .remade-info { font-size: 12px !important; }
            .remake-cases-list { font-size: 12px !important; }
          }

          @media (max-width: 768px) {
            .case-header-container { display: flex !important; flex-direction: row !important; flex-wrap: wrap !important; align-items: center !important; justify-content: space-between !important; width: 95% !important; margin: 8px auto !important; gap: 4px !important; }
            .case-title-section { display: flex !important; flex-wrap: wrap !important; align-items: center !important; gap: 3px !important; }
            .case-number { font-size: 14px !important; margin-bottom: 0 !important; }
            .invoice-number { font-size: 14px !important; margin-bottom: 0 !important; }
            .invoice-number a, .invoice-number span { font-size: 14px !important; }
            .outsourced-badge { font-size: 7px !important; padding: 1px 5px !important; }
            .cancelled-badge div { font-size: 12px !important; padding: 2px 5px !important; }
            .action-buttons-container { display: flex !important; flex-direction: row !important; gap: 3px !important; align-items: center !important; justify-content: flex-end !important; flex-wrap: nowrap !important; margin-left: auto !important; margin-top: 0 !important; margin-bottom: 0 !important; align-self: center !important; }
            .action-buttons-container > * { margin-top: 0 !important; margin-bottom: 0 !important; align-self: center !important; vertical-align: middle !important; }
            .action-buttons-container > button, .action-buttons-container > .btn, .action-buttons-container button, .action-buttons-container .btn { margin-top: 0 !important; margin-bottom: 0 !important; transform: none !important; align-self: center !important; }
            .action-buttons-container > div { margin-top: 0 !important; margin-bottom: 0 !important; align-self: center !important; }
            .action-buttons-container > div > button, .action-buttons-container > div > .btn { margin-top: 0 !important; margin-bottom: 0 !important; align-self: center !important; }
            .action-buttons-container button, .action-buttons-container .btn { font-size: 6px !important; padding: 1px 3px !important; min-height: 16px !important; min-width: 25px !important; }
            .action-buttons-container > *:first-child { transform: translateY(-4px) !important; }
            .reference-info { font-size: 8px !important; margin-top: 0 !important; }
            .remade-info { font-size: 8px !important; margin-top: 0 !important; }
            .main-content-container { margin-top: 4px !important; width: 95% !important; padding: 0 !important; }
            .responsive-row { display: flex !important; flex-wrap: wrap !important; align-items: flex-start !important; gap: 3px !important; margin-bottom: 3px !important; width: 100% !important; margin: 0 !important; padding: 0 !important; }
            .responsive-col { flex: 1 1 calc(50% - 3px) !important; min-width: 0 !important; max-width: calc(50% - 3px) !important; margin: 0 !important; padding: 0 !important; align-self: flex-start !important; vertical-align: top !important; position: relative !important; top: 0 !important; box-sizing: border-box !important; }
            .account-col, .patient-col { flex: 1 1 calc(50% - 3px) !important; min-width: 0 !important; max-width: calc(50% - 3px) !important; margin: 0 !important; padding: 0 !important; align-self: flex-start !important; vertical-align: top !important; position: relative !important; top: 0 !important; }
            .account-col { margin-right: 0 !important; padding-right: 0 !important; }
            .patient-col { margin-left: 0 !important; padding-left: 0 !important; align-self: flex-start !important; top: 0 !important; }
            .patient-col .patient-label-wrapper { transform: translate(0px, -2px) !important; margin-top: 0 !important; padding-top: 0 !important; }
            .patient-col > div { margin-top: 0 !important; padding-top: 0 !important; align-self: flex-start !important; }
            .patient-col > div > div { margin-top: 0 !important; padding-top: 0 !important; }
            .provider-col, .appointment-col { align-self: flex-start !important; vertical-align: top !important; margin-top: 0 !important; padding-top: 0 !important; position: relative !important; }
            .appointment-col { top: -2px !important; }
            .provider-col > div, .appointment-col > div { margin-top: 0 !important; padding-top: 0 !important; }
            .color-col { width: auto !important; min-width: 0 !important; max-width: calc(50% - 3px) !important; margin-right: 0 !important; padding-right: 0 !important; align-self: flex-start !important; vertical-align: top !important; top: 0 !important; }
            .status-col { align-self: flex-start !important; vertical-align: top !important; margin-top: 0 !important; padding-top: 0 !important; position: relative !important; top: -2px !important; }
            .color-col > div { margin-top: 0 !important; padding-top: 0 !important; }
            .status-col > div { margin-top: 0 !important; padding-top: 0 !important; }
            .shipment-col { margin-right: 0 !important; padding-right: 0 !important; }
            .responsive-col .fa, .responsive-col .fas, .responsive-col .far, .responsive-col .fab, .responsive-col .material-icons, .responsive-col .MuiIcon-root, .responsive-col svg, .responsive-col i, .responsive-col .icon, .responsive-col [class*="icon-"], .responsive-col [class*="fa-"], .responsive-col .MuiSvgIcon-root { font-size: 5px !important; width: 5px !important; height: 5px !important; min-width: 5px !important; min-height: 5px !important; max-width: 5px !important; max-height: 5px !important; }
            .responsive-col * .fa, .responsive-col * .fas, .responsive-col * .far, .responsive-col * .fab, .responsive-col * .material-icons, .responsive-col * .MuiIcon-root, .responsive-col * svg, .responsive-col * i, .responsive-col * .icon, .responsive-col * [class*="icon-"], .responsive-col * [class*="fa-"], .responsive-col * .MuiSvgIcon-root { font-size: 5px !important; width: 5px !important; height: 5px !important; min-width: 5px !important; min-height: 5px !important; max-width: 5px !important; max-height: 5px !important; }
            .responsive-col [class*="fa-"], .responsive-col [class*="icon-"], .responsive-col [class^="fa-"], .responsive-col [class^="icon-"] { font-size: 5px !important; }
            .responsive-col svg { width: 5px !important; height: 5px !important; min-width: 5px !important; min-height: 5px !important; max-width: 5px !important; max-height: 5px !important; }
            .production-col .fa, .production-col .fas, .production-col .far, .production-col .fab, .production-col .material-icons, .production-col .MuiIcon-root, .production-col svg, .production-col i, .production-col .icon, .production-col [class*="icon-"], .production-col [class*="fa-"], .production-col .MuiSvgIcon-root, .shipping-col .fa, .shipping-col .fas, .shipping-col .far, .shipping-col .fab, .shipping-col .material-icons, .shipping-col .MuiIcon-root, .shipping-col svg, .shipping-col i, .shipping-col .icon, .shipping-col [class*="icon-"], .shipping-col [class*="fa-"], .shipping-col .MuiSvgIcon-root { font-size: 5px !important; width: 5px !important; height: 5px !important; min-width: 5px !important; min-height: 5px !important; max-width: 5px !important; max-height: 5px !important; }
            .production-col svg, .shipping-col svg { width: 5px !important; height: 5px !important; min-width: 5px !important; min-height: 5px !important; max-width: 5px !important; max-height: 5px !important; }
            .production-col [class*="fa-"], .production-col [class*="icon-"], .shipping-col [class*="fa-"], .shipping-col [class*="icon-"] { font-size: 5px !important; }
            .responsive-col * { font-size: 7px !important; }
            .production-col *, .shipping-col * { font-size: 7px !important; }
            .remake-cases-list { font-size: 8px !important; margin-bottom: 4px !important; gap: 4px !important; }
            .remake-cases-list span { font-size: 8px !important; }
            .outsourced-case-banner { font-size: 8px !important; padding: 5px 7px !important; margin-bottom: 5px !important; gap: 5px !important; }
            .outsourced-case-banner span { font-size: 8px !important; }
            .production-shipping-row { display: flex !important; flex-wrap: wrap !important; align-items: flex-start !important; gap: 3px !important; width: 100% !important; margin: 0 !important; padding: 0 !important; }
            .production-col, .shipping-col { flex: 1 1 calc(50% - 3px) !important; min-width: 0 !important; max-width: calc(50% - 3px) !important; padding: 0 !important; margin: 0 !important; align-self: flex-start !important; box-sizing: border-box !important; }
            .production-col .form-control, .shipping-col .form-control, .production-col .form-select, .shipping-col .form-select { font-size: 7px !important; padding: 1px 2px !important; height: auto !important; min-height: 18px !important; }
            .production-col .btn, .shipping-col .btn { font-size: 6px !important; padding: 1px 3px !important; }
            .production-col .card-body, .shipping-col .card-body { padding: 3px !important; }
            .production-col th, .production-col td, .shipping-col th, .shipping-col td { padding: 1px 2px !important; font-size: 6px !important; }
            .production-col .card-header, .shipping-col .card-header { padding: 2px 3px !important; font-size: 7px !important; }
            .action-buttons-container .fa, .action-buttons-container .fas, .action-buttons-container .far, .action-buttons-container .fab, .action-buttons-container .material-icons, .action-buttons-container .MuiIcon-root, .action-buttons-container svg, .action-buttons-container i, .action-buttons-container .icon, .action-buttons-container [class*="icon-"], .action-buttons-container [class*="fa-"], .action-buttons-container .MuiSvgIcon-root { font-size: 4px !important; width: 4px !important; height: 4px !important; min-width: 4px !important; min-height: 4px !important; max-width: 4px !important; max-height: 4px !important; }
            .action-buttons-container svg { width: 4px !important; height: 4px !important; min-width: 4px !important; min-height: 4px !important; max-width: 4px !important; max-height: 4px !important; }
            .responsive-col .case-tab .fa, .responsive-col .case-tab .fas, .responsive-col .case-tab .far, .responsive-col .case-tab .fab, .responsive-col .case-tab .material-icons, .responsive-col .case-tab svg, .responsive-col .case-tab i { font-size: 5px !important; width: 5px !important; height: 5px !important; min-width: 5px !important; min-height: 5px !important; max-width: 5px !important; max-height: 5px !important; }
            .responsive-col .case-tab svg { width: 5px !important; height: 5px !important; }
            .responsive-col .label-icon, .responsive-col [class*="label-"] .fa, .responsive-col [class*="label-"] .fas, .responsive-col [class*="label-"] .far, .responsive-col [class*="label-"] svg { font-size: 4px !important; width: 4px !important; height: 4px !important; min-width: 4px !important; min-height: 4px !important; max-width: 4px !important; max-height: 4px !important; }
            .tag-label-wrapper, .shipment-label-wrapper { width: 100% !important; }
            .tag-label-wrapper *, .shipment-label-wrapper * { font-size: 7px !important; }
            .case-tabs-row { margin-top: 0 !important; padding-top: 0 !important; }
            .case-tabs-row > .col-lg-12 { margin-top: 0 !important; padding-top: 0 !important; }
            .upload-modal-minimized-bar { bottom: 10px !important; right: 10px !important; padding: 6px 10px !important; font-size: 10px !important; gap: 6px !important; border-radius: 8px !important; }
            .upload-modal-minimized-bar span { font-size: 10px !important; }
            .upload-modal-minimized-bar span:first-child { font-size: 14px !important; }
          }
        `}
      </style>
    </div>
  );
};

export default Case;